/**
 * @description A helper class to manage service workers and their lifecycles
 * @export
 * @class ServiceWorkerHelper
 */
export default class ServiceWorkerHelper {
  /**
   * Creates an instance of ServiceWorker.
   * @memberof ServiceWorkerHelper
   */
  constructor() {
    this.#registerUpdateListeners();
  }

  /**
   * @description Registers a service worker with the given path and options
   * @param {String} serviceWorkerPath  The path to the service worker file
   * @param {Object} [options={}]       The options to pass to the registration
   * @return {Object} The registration object
   * @memberof ServiceWorkerHelper
   */
  async registerServiceWorker(serviceWorkerPath, options = {}) {
    // Check if the service worker API is available (not in private tabs or older browsers)
    if (!("serviceWorker" in window.navigator)) {
      return null;
    }
    options = { ...options, scope: "/", type: "module" };
    return await window.navigator.serviceWorker.register(serviceWorkerPath, options);
  }

  /**
   * @description Registers the update listeners for the currently available service workers
   * @note This uses Promises without async/await so that it can be called in the constructor
   * @private
   * @memberof ServiceWorkerHelper
   */
  #registerUpdateListeners() {
    // Check if the service worker API is available (not in private tabs or older browsers)
    if (!("serviceWorker" in window.navigator)) {
      return;
    }

    // Get all registrations
    window.navigator.serviceWorker.getRegistrations().then((registrations) => {
      // Loop through each registration and add the event listener
      for (const registration of registrations) {
        this.addUpdateListener(registration);
      }
    });
  }

  /**
   * @description Adds an event listener for the updatefound event on the given registration
   * @param {Object} registration The registration object
   * @memberof ServiceWorkerHelper
   */
  addUpdateListener(registration) {
    // Check if the service worker API is available (not in private tabs or older browsers)
    if (!("serviceWorker" in window.navigator)) {
      return;
    }

    registration.addEventListener("updatefound", () => {
      if (registration.installing) {
        registration.installing.addEventListener("statechange", () => {
          if (registration.waiting) {
            if (window.navigator.serviceWorker.controller) {
              this.invokeServiceWorkerUpdateFlow(registration);
            }
          }
        });
      }
    });

    let refreshing = false;

    window.navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!refreshing) {
        window.location.reload();
        refreshing = true;
      }
    });
  }

  /**
   * @description Invokes the service worker update flow and UX logic
   * @param {Object} registration The registration object
   * @memberof ServiceWorkerHelper
   */
  invokeServiceWorkerUpdateFlow(registration) {
    // Check if the service worker API is available (not in private tabs or older browsers)
    if (!("serviceWorker" in window.navigator)) {
      return;
    }
    // TODO: Implement UX and logic here
  }

  /**
   * @description Gets the current status of the service worker.
   * @return {string} The status of the service worker ('installing', 'installed', 'active', or 'unavailable').
   * @memberof ServiceWorkerHelper
   */
  async getStatus() {
    // Check if the service worker API is available
    if (!("serviceWorker" in window.navigator)) {
      return "unavailable";
    }

    // Get the service worker registration
    const status = await window.navigator.serviceWorker.getRegistration();

    if (status.installing) {
      return "installing";
    } else if (status.waiting) {
      return "installed";
    } else if (status.active) {
      return "active";
    } else {
      return "unavailable"; // Or any other appropriate status
    }
  }
}
